import type { IFluidCodeDetails } from '@fluidframework/core-interfaces';
import {
    CancellationError,
    ComponentLoaderData,
    NotificationService,
    ShareService,
} from '@fluidx/office-fluid-types';
import { createOdspCreateContainerRequest } from '@fluidframework/odsp-driver/lib/createOdspCreateContainerRequest';
import {
    CreateNewTracker,
    DriverInfo,
    LoadInIframeResult,
    LoadInSourcelessIframeResult,
    OfficeWebHostScope,
} from '@ms/office-web-host';
import { PerformanceDatapoint } from 'owa-analytics';
import { getSessionId } from 'owa-config';
import { timestamp } from 'owa-datetime';
import {
    fluidComponentCancelled,
    fluidComponentError,
    fluidComponentLoaded,
} from 'owa-fluid-datapoints';
import { reportPerfCheckmark, OWAFluidCheckmarksEnum } from 'owa-fluid-perf-datapoints';
import { FluidLoadOwaScenario, FluidOwaSource, FluidLoadSource } from 'owa-fluid-validations';
import {
    onFluidFileInserted,
    onFluidFileCreationFailed,
    onFluidFileInsertionStart,
} from 'owa-fluid-link';
import ReactDOM from 'react-dom';
import { Deferred } from './Deferred';
import { getDriverDetailsForFluidItem } from './getDriverDetailsForFluidItem';
import { createFluidTargetAndHeader } from './createFluidTargetAndHeader';
import { renderFluidComponent } from '../../components/FluidComponent';
import { getFluidPropsForRender } from '../fluidProps/getFluidPropsForRender';
import {
    getNotificationServicePromise,
    getSharedScope,
    getShareServicePromise,
} from '../fluidProps/scopeHelpers';
import {
    isFluidContainerConfigForEditor,
    isFluidContainerConfigForDiv,
    FluidContainerConfig,
} from '../../store/schema/FluidContainerConfig';
import { getFluidScenarioName } from './getFluidScenarioName';
import { getFluidScenarioEntryPoint } from './getFluidScenarioEntryPoint';

// Creates and mounts a new fluid component in an editor/containerDiv. Handles insertion and clean up.
export async function mountNewFluidComponent(
    containerConfig: FluidContainerConfig,
    originalNode: Node,
    driverInfo: DriverInfo,
    codeDetails: IFluidCodeDetails,
    scenarioStartTime: number,
    owaSource: FluidOwaSource,
    addRecipientFunc: (address: string) => void,
    heroComponentConfiguration?: ComponentLoaderData | undefined,
    viewStateId?: string,
    fromExistingTemplateUrl?: string
) {
    const scenarioLifecycle = 'Create';
    const scenarioName = getFluidScenarioName(owaSource);
    const scenarioEntryPoint = getFluidScenarioEntryPoint(owaSource);
    const perfDatapoint = new PerformanceDatapoint('FluidMountNewComponent');
    perfDatapoint.addCustomData({
        differenceFromStartTime: timestamp() - scenarioStartTime,
        owaSource: owaSource,
    });

    const containerId = containerConfig.containerId;

    onFluidFileInsertionStart(containerId, owaSource, viewStateId);

    let editor, containerDiv;
    if (isFluidContainerConfigForDiv(containerConfig)) {
        containerDiv = containerConfig.containerDiv;
    } else if (isFluidContainerConfigForEditor(containerConfig)) {
        editor = containerConfig.editor;
    }

    const [targetDiv, headerNode] = createFluidTargetAndHeader(
        editor,
        containerDiv,
        originalNode,
        containerId
    );

    // Get OWA's session Id from config
    const sessionId = getSessionId();
    const removeElementCallback = () => {
        ReactDOM.unmountComponentAtNode(targetDiv);
    };
    const mountComponentProps = getFluidPropsForRender(
        sessionId,
        owaSource,
        scenarioLifecycle,
        scenarioName,
        scenarioEntryPoint,
        scenarioStartTime,
        codeDetails,
        undefined /* url */,
        removeElementCallback,
        containerConfig.customMenuItems
    );
    const notificationServicePromise = new Deferred<NotificationService>();
    const shareServicePromise = new Deferred<ShareService>();

    const scopeBase = getSharedScope(mountComponentProps.scopeProps);
    const scope: OfficeWebHostScope = {
        NotificationServicePromise: notificationServicePromise,
        ShareServicePromise: shareServicePromise,
        ...scopeBase,
    };
    let result: LoadInIframeResult | null = null;
    try {
        result = await new Promise(resolve => {
            renderFluidComponent(
                mountComponentProps,
                scope,
                targetDiv,
                driverInfo,
                headerNode,
                resolve,
                owaSource,
                true, //isCreateNew
                containerConfig.controlNode,
                heroComponentConfiguration,
                true, //autoFocus
                viewStateId,
                containerId,
                fromExistingTemplateUrl
            );
        });
        const loadedInfo = await result!.loadedInfo;
        perfDatapoint.addCheckmark('fetchedLoadedInfo');
        containerConfig.onComponentLoadedInIframe?.(loadedInfo);
        reportPerfCheckmark(OWAFluidCheckmarksEnum.componentAndContainerBootStarted, owaSource);
        const [component, container] = await Promise.all([
            loadedInfo.component,
            loadedInfo.container,
        ]);
        perfDatapoint.addCheckmark('componentAndContainerResolved');
        const createNewTracker = new CreateNewTracker(mountComponentProps.hostConfig.logger!, {
            hostScenarioStartTime: scenarioStartTime,
        });
        reportPerfCheckmark(OWAFluidCheckmarksEnum.componentAndContainerResolved, owaSource);
        const newFileInfo = await driverInfo.newFileInfoPromise;
        const containerRequest = createOdspCreateContainerRequest(
            newFileInfo.siteUrl,
            newFileInfo.driveId,
            newFileInfo.filePath,
            newFileInfo.filename
        );

        let retryAttach = false;
        await container.attach(containerRequest).catch(error => {
            retryAttach = true;
        });
        if (retryAttach) {
            // attach can fail due to network or service interruptions, per fluid team guidance we retry once
            await container.attach(containerRequest).catch(error => {
                // We do not retry a third time, as success is unlikely after two failures.
                container.close();
                createNewTracker.setFailure(true /*onAttach*/, error);
                throw error;
            });
        }
        perfDatapoint.addCheckmark('attachedContainer');

        const loadable = component?.IFluidLoadable;
        const relativeUrl = loadable?.handle.absolutePath || '/';
        let url = await container.getAbsoluteUrl(relativeUrl).catch(error => {
            /* swallow the error and retry */
        });
        if (!url) {
            // getAbsoluteUrl can fail due to network or service interruptions, per fluid team guidance we retry once
            url = await container.getAbsoluteUrl(relativeUrl).catch(error => {
                container.close();
                createNewTracker.setFailure(false /*onAttach*/, error);
                throw error;
            });
        }

        // If the url is undefined, close the container and call the CreateNewTracker's setFailure method
        if (!url) {
            container.close();
            createNewTracker.setFailure(
                false /*onAttach*/,
                new Error('container.getAbsoluteUrl returned undefined')
            );
            throw new Error('Could not generate url for fluid component');
        }

        perfDatapoint.addCheckmark('retrieved url');
        createNewTracker.setSuccess();
        const resolvedDriverInfo = getDriverDetailsForFluidItem(url);
        perfDatapoint.addCheckmark('fetchedDriverDetails');
        reportPerfCheckmark(OWAFluidCheckmarksEnum.driverDetailsFetched, owaSource);

        if (resolvedDriverInfo) {
            notificationServicePromise.resolve(
                getNotificationServicePromise(
                    mountComponentProps.scopeProps.tokenProvider,
                    resolvedDriverInfo.itemId,
                    resolvedDriverInfo.driveId,
                    resolvedDriverInfo.siteUrl,
                    mountComponentProps.hostConfig.logger,
                    sessionId,
                    addRecipientFunc
                )
            );
            shareServicePromise.resolve(
                getShareServicePromise(
                    mountComponentProps.scopeProps.tokenProvider,
                    mountComponentProps.hostConfig.logger,
                    resolvedDriverInfo.itemId,
                    resolvedDriverInfo.driveId,
                    resolvedDriverInfo.siteUrl,
                    addRecipientFunc
                )
            );
        } else {
            const errorMessage = 'GetLocatorFromOdspUrl failed to return driverInfo object';
            const error = new Error(errorMessage);
            notificationServicePromise.reject(error);
            shareServicePromise.reject(error);
        }

        onFluidFileInserted(containerId, url);

        fluidComponentLoaded(
            FluidLoadOwaScenario.ComposeWithNewComponent,
            owaSource,
            FluidLoadSource.Fluid,
            scenarioStartTime
        );
        perfDatapoint.end();
    } catch (error) {
        if (editor && !editor.isDisposed()) {
            editor.deleteNode(targetDiv);
        }
        if (containerDiv) {
            ReactDOM.unmountComponentAtNode(containerDiv);
        }
        onFluidFileCreationFailed(containerId, owaSource, viewStateId);

        // If the getIframe method is defined, but it returns undefined that means that the component has been unmounted or disposed
        // In this case we should treat any error like a cancellation error.
        const isIFrameUnmounted =
            (result as LoadInSourcelessIframeResult)?.getIframe &&
            !(result as LoadInSourcelessIframeResult).getIframe();
        if (error instanceof CancellationError || isIFrameUnmounted) {
            fluidComponentCancelled(
                FluidLoadOwaScenario.ComposeWithNewComponent,
                owaSource,
                scenarioStartTime
            );
            perfDatapoint.invalidate();
            return;
        }
        fluidComponentError(
            FluidLoadOwaScenario.ComposeWithNewComponent,
            owaSource,
            FluidLoadSource.Fluid,
            error
        );
        perfDatapoint.endWithError(error);
    }
}
